import { Box, Icon, Stack, Typography } from '@mui/material';
import { DropdownSelect, DropdownSelectRow } from '@schooly/components/filters';
import { useCellStates } from '@schooly/components/data-grid';
import { CalendarIcon, ExpandableSearch, Loading } from '@schooly/style';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { AttendanceGrid } from './common/AttendanceGrid';
import { AttendanceGridShell } from './AttendanceSidebar/AttendanceGridShell';
import { AttendanceGroupDayHeaderCell } from './common/AttendanceCells';
import { ATTENDANCE_GROUP_STYLING } from './common/attendanceStyling';
import { getWeeksWithStudents, WeekOption } from './common/getWeeksWithStudents';
import { AttendanceCellState, AttendanceGridColumn } from './common/types';
import { useAttendanceGridForGroup } from './data/useAttendanceGridForGroup';
import {
  useAttendanceWeekDropdown,
  UseAttendanceWeekDropdownResult,
} from './data/useAttendanceWeek';
import { GroupGridMember } from './common/buildGroupGrid';
import { useGroupStudentsForValidity } from './data/useGroupStudentsForValidity';
import { isUserMatchedTheSearch } from '@schooly/utils/user-helpers';
import { Group, GroupType } from '@schooly/api';
import { AttendanceSubjectPreviewTable } from './AttendanceSubjectPreviewTable';
import { GroupPreviewAttendanceEmptyStubNoButton } from '../../Groups/GroupPreviewModal/GroupPreviewModalAttendance/GroupPreviewAttendanceEmptyStub/GroupPreviewAttendanceEmptyStubNoButton';
import { ModalEmpty } from '../../../components/uikit-components/Modal/ModalEmpty';

const renderGroupDayHeader = (column: AttendanceGridColumn) => (
  <AttendanceGroupDayHeaderCell column={column} />
);

export const AttendanceGroupPreviewTable: FC<{
  group: Group;
}> = ({ group }) => {
  const isSubject = group.group_type === GroupType.Subject;
  const { members, isLoading: isLoadingMembers } = useGroupStudentsForValidity(group);

  const weekOptions = useMemo(
    () => (isSubject ? [] : getWeeksWithStudents(members, group.validity)),
    [isSubject, members, group.validity],
  );

  const week = useAttendanceWeekDropdown(weekOptions);
  if (isLoadingMembers) return <Loading />;

  if (isSubject) {
    return <AttendanceSubjectPreviewTable group={group} members={members} />;
  }

  if (!week.weekDates.length) {
    return (
      <AttendanceGridShell codes={[]}>
        <GroupPreviewAttendanceEmptyStubNoButton />
      </AttendanceGridShell>
    );
  }

  return <AttendanceGroupPreviewTableContent group={group} members={members} week={week} />;
};

const AttendanceGroupPreviewTableContent: FC<{
  group: Group;
  members: GroupGridMember[];
  week: UseAttendanceWeekDropdownResult;
}> = ({ group, members, week }) => {
  const { formatMessage } = useIntl();
  const dropdown = useRef<DropdownSelect | null>(null);

  const cellContext = useCellStates<AttendanceCellState>();
  const [search, setSearch] = useState('');

  const { columns, rows, codes, isTableLoading } = useAttendanceGridForGroup(
    group.id,
    week.weekDates,
    members,
  );

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? rows.filter((row) => isUserMatchedTheSearch(row.student, query)) : rows;
  }, [rows, search]);

  const handleSelectWeek = useCallback(
    (option: WeekOption) => {
      week.onSelectWeek(option);
      dropdown.current?.close();
    },
    [week.onSelectWeek],
  );

  const renderWeekDropdownContent = useCallback(
    () => (
      <Stack gap={0.25} p={1}>
        {week.weekOptions.map((option) => {
          const isSelected = option.id === week.selectedOption?.id;
          return (
            <Box
              key={option.id}
              ref={
                isSelected
                  ? (el: HTMLDivElement | null) => el?.scrollIntoView({ block: 'center' })
                  : undefined
              }
            >
              <DropdownSelectRow isSelected={isSelected} onClick={() => handleSelectWeek(option)}>
                {option.name}
              </DropdownSelectRow>
            </Box>
          );
        })}
      </Stack>
    ),
    [week.weekOptions, week.selectedOption, handleSelectWeek],
  );

  const renderContent = useCallback(() => {
    if (!isTableLoading && !columns.length) {
      return <GroupPreviewAttendanceEmptyStubNoButton />;
    }

    if (search.trim() && !filteredRows.length) {
      return <ModalEmpty />;
    }

    return (
      <AttendanceGrid
        rows={filteredRows}
        gridColumns={columns}
        styling={ATTENDANCE_GROUP_STYLING}
        primaryHeaderLabel={formatMessage({ id: 'section-Students' })}
        renderDayHeader={renderGroupDayHeader}
        getCellState={cellContext.getCellState}
      />
    );
  }, [isTableLoading, filteredRows, columns, cellContext.getCellState, search, formatMessage]);

  return (
    <AttendanceGridShell codes={codes} groupId={group.id}>
      <Stack direction="row" alignItems="center" gap={1} justifyContent="flex-end" sx={{ mb: 1 }}>
        <ExpandableSearch
          value={search}
          onChange_MemoizedCallbackOnly={setSearch}
          placeholder={formatMessage({ id: 'people-Search' })}
          withDebounce
          disabled={isTableLoading}
        />

        <DropdownSelect
          ref={dropdown}
          hasValues={!!week.selectedOption}
          renderContent={renderWeekDropdownContent}
          borderOnHover
          maxExpandedContainerWidth={500}
          startIcon={
            <Icon sx={{ color: 'common.grey2' }}>
              <CalendarIcon />
            </Icon>
          }
        >
          {() => (
            <Typography variant="h3" sx={{ color: 'common.grey2' }} noWrap>
              {week.selectedOption?.name}
            </Typography>
          )}
        </DropdownSelect>
      </Stack>

      {renderContent()}
    </AttendanceGridShell>
  );
};
